import { CoreApi, ErrorMessages, Product } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, Vector, WorldTransformComponent } from '@inter-ikea-core/core';
import { BehaviorParameters } from 'rangeConfig/@types';
import { ProductType } from '@inter-ikea-storageone/range';
import { HierarchyUtils } from '@inter-ikea-core/core';
import { getFrameConnectorsByKey } from '../utils/utils';
import { enableDarkModeIfLightsInScene } from 'rangeConfig/shared/utils/darkMode';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import {
  hideAddingLightsToBestaMixedDoorsErrorMessage,
  hideAddingLightsToBestaSolidDoorsErrorMessage,
  hideMixedBestaFramesInSceneErrorMessage,
  hideNoBestaFramesInSceneErrorMessage
} from '../rules/hideMenuRules';

// Compare coordinates with threshold because of inexact values for drawer fronts
const looseCoordinateMatch = (value1: number, value2: number, threshold = 5) =>
  Math.abs(value1 - value2) < threshold;

const minVerticalDistance = 240; // mm

const autoFillLightsBehavior = ({
  coreApi,
  rootState: {
    products: { inMarket: products, selectedGroupId },
    layout: { darkScene }
  },
  props
}: BehaviorParameters) => {
  const determinePlacementAttemptErrorMessage = () => {
    if (!hideNoFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.NoFramesInScene;
    } else if (!hideNoBestaFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.AddingBestaToEketFrames;
    } else if (!hideAddingLightsToBestaSolidDoorsErrorMessage(coreApi)) {
      return ErrorMessages.AddingLightsToBestaSolidDoors;
    } else if (!hideAddingLightsToBestaMixedDoorsErrorMessage(coreApi)) {
      return ErrorMessages.AddingLightsToBestaMixedDoors;
    } else if (!hideMixedBestaFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.AddingToMixedBestaEketFrames;
    }
    return null;
  };
  const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
  if (placementAttemptErrorMessage) {
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }
  const productId = props.coreProductIds?.length ? props.coreProductIds[0] : props.coreProductId;
  autoFillInnerLights(productId, coreApi, products);
  enableDarkModeIfLightsInScene(coreApi, darkScene, selectedGroupId);
};

const autoFillInnerLights = (productId: string, coreApi: CoreApi, products: Product[]) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  const isInnerLight = (entity: Entity) =>
    coreApi.entities.isProductType(entity, ProductType.BESTA_INNER_LIGHT);

  let oldLights: Entity[] = [];
  const targets: Entity[] = [];
  const targetConnectors: { frame: Entity; key: string }[] = [];

  if (selectedEntity) {
    if (isInnerLight(selectedEntity)) {
      oldLights = [selectedEntity];
    } else {
      oldLights = HierarchyUtils.children(selectedEntity).filter(isInnerLight);
    }
  } else {
    oldLights = coreApi.entities.getByProductType(ProductType.BESTA_INNER_LIGHT);
    const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

    const getWorldPosition = (entity: Entity) => coreApi.entities.getWorldPosition(entity);

    const getEntitySize = (entity: Entity) => {
      const size = coreApi.entities.getEntityComSize(entity);
      return size ? size : { width: 0, height: 0, depth: 0 };
    };

    const isGlassDoor = (entity: Entity) => {
      const productId = coreApi.entities.getProductRef(entity);
      return !!productId && coreApi.products.isGlassDoor(productId, products);
    };

    frames.forEach(frame => {
      const lightConnectorsOnFrame = getFrameConnectorsByKey(frame, 'inner-light');

      const frameFronts = coreApi.entities
        .getFrameDescendants(frame)
        .filter(e => e.hasComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID));

      const frameShelves = coreApi.entities
        .getFrameDescendants(frame)
        .filter(e => coreApi.entities.isProductType(e, ProductType.BESTA_SHELF));

      const topFrameFronts = frameFronts.filter(front => {
        const frameTop = getWorldPosition(frame).Y + getEntitySize(frame).height / 2;
        const frontTop = getWorldPosition(front).Y + getEntitySize(front).height / 2;

        return looseCoordinateMatch(frameTop, frontTop);
      });

      const getCloseEntities = (point: Vector) => {
        const close = coreApi.entities
          .getEntitiesInDirection(point, new Vector(0, -1, 0), frame)
          .map(hit => (hit.distance < minVerticalDistance ? hit.entity : null))
          .filter(Boolean) as Entity[];

        // Ignore glass shelves when finding close entities
        return close.filter(e => !coreApi.entities.isProductType(e, ProductType.BESTA_GLASS_SHELF));
      };

      const frameTransformPosition = (position: Vector) =>
        frame
          .getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
          ?.transform.transformPosition(position) as Vector;

      const getFrameTransformedPosition = (entity: Entity) =>
        frameTransformPosition(getWorldPosition(entity));

      lightConnectorsOnFrame?.forEach(connector => {
        const connectorPos = connector.getWorldPositionFromOwnerEntity(frame);

        if (getCloseEntities(connectorPos).length) return;

        // Front is covering connector if their frame transformed X is equal
        const coveringFront = topFrameFronts.find(d =>
          looseCoordinateMatch(
            getFrameTransformedPosition(d).X,
            frameTransformPosition(connectorPos).X
          )
        );

        if (!coveringFront || isGlassDoor(coveringFront))
          targetConnectors.push({ frame, key: connector.key });
      });

      frameShelves.forEach(shelf => {
        const coveringFront = coreApi.entities.getCoveringFronts(shelf)[0]?.entity;
        const shelfPosition = coreApi.entities.getWorldPosition(shelf);
        const closeEntities = getCloseEntities(shelfPosition);
        const posBelow = shelfPosition.plus(new Vector(0, -minVerticalDistance / 2, 0));
        const frontBelow = coreApi.entities
          .getEntitiesInDirection(posBelow, new Vector(0, 0, 1), frame)
          .filter(hit => hit.entity.hasComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID))[0]?.entity;

        if (closeEntities.length) return;

        if (coveringFront && !isGlassDoor(coveringFront) && frontBelow && !isGlassDoor(frontBelow))
          return;

        targets.push(shelf);
      });
    });
  }

  const placementResult = coreApi.products.autoFillInnerLights(productId, targets, oldLights);

  targetConnectors.forEach(({ frame, key }) => {
    coreApi.products.addToConnector(productId, frame, 'attach', key);
  });

  if (selectedEntity && isInnerLight(selectedEntity)) {
    const placedEntity = placementResult[0].placedEntity;

    if (placedEntity) {
      coreApi.entities.select(placedEntity);
    }
  }
};

export default autoFillLightsBehavior;
